import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy, TrendingUp, Heart, Award } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserStats = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserStats();
  }, [userId]);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/user/${userId}`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching user stats:", error);
      toast.error("Error al cargar estadísticas del usuario");
    } finally {
      setLoading(false);
    }
  };

  const getBadgeClass = (badge) => {
    const badgeMap = {
      "Lectora": "badge-lectora",
      "Activa": "badge-activa",
      "Influencer": "badge-influencer",
      "Leyenda Viva": "badge-leyenda"
    };
    return `badge ${badgeMap[badge] || 'badge-lectora'}`;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-20 text-slate-400" data-testid="loading-stats">
          Cargando estadísticas...
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="page-container">
        <div className="text-center py-20 text-slate-400" data-testid="no-stats">
          Usuario no encontrado
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Button
        variant="ghost"
        onClick={() => navigate('/')}
        className="mb-4 text-purple-300 hover:text-purple-200"
        data-testid="back-to-dashboard"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver al Dashboard
      </Button>

      <div className="page-header">
        <h1 className="page-title" data-testid="user-stats-title">
          {stats.username || stats.first_name || `Usuario ${stats.user_id}`}
        </h1>
        <div className={`${getBadgeClass(stats.badge)} text-lg`} data-testid="user-badge">
          {stats.badge}
        </div>
      </div>

      <div className="stats-grid">
        <Card className="glass-card border-slate-700" data-testid="current-points-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <Trophy className="w-12 h-12 mx-auto mb-3 text-purple-400" />
              <div className="stat-value" data-testid="current-month-points">{stats.current_month_points}</div>
              <div className="stat-label">Puntos este mes</div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-slate-700" data-testid="historical-points-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 text-blue-400" />
              <div className="stat-value" data-testid="historical-points">{stats.historical_points}</div>
              <div className="stat-label">Puntos históricos</div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-slate-700" data-testid="carisma-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <Heart className="w-12 h-12 mx-auto mb-3 text-pink-400" />
              <div className="stat-value" data-testid="carisma-available">{stats.carisma_available}</div>
              <div className="stat-label">Carisma disponible</div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-slate-700" data-testid="rank-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <Award className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
              <div className="stat-value" data-testid="current-rank">#{stats.current_rank}</div>
              <div className="stat-label">Ranking actual</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card border-slate-700 mt-8" data-testid="progression-card">
        <CardHeader>
          <CardTitle className="ranking-title">📈 Progresión de Badges</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-300">Lectora</span>
              <span className={stats.current_month_points >= 0 ? 'text-green-400' : 'text-slate-500'}>✓</span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-slate-400 to-slate-500 transition-all"
                style={{ width: `${Math.min((stats.current_month_points / 50) * 100, 100)}%` }}
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-300">Activa (51 pts)</span>
              <span className={stats.current_month_points >= 51 ? 'text-green-400' : 'text-slate-500'}>
                {stats.current_month_points >= 51 ? '✓' : `${51 - stats.current_month_points} pts restantes`}
              </span>
            </div>
            {stats.current_month_points >= 51 && (
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-400 to-blue-500 transition-all"
                  style={{ width: `${Math.min(((stats.current_month_points - 51) / 100) * 100, 100)}%` }}
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-slate-300">Influencer (151 pts)</span>
              <span className={stats.current_month_points >= 151 ? 'text-green-400' : 'text-slate-500'}>
                {stats.current_month_points >= 151 ? '✓' : `${151 - stats.current_month_points} pts restantes`}
              </span>
            </div>
            {stats.current_month_points >= 151 && (
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-400 to-pink-500 transition-all"
                  style={{ width: `${Math.min(((stats.current_month_points - 151) / 150) * 100, 100)}%` }}
                />
              </div>
            )}

            <div className="flex items-center justify-between">
              <span className="text-slate-300">Leyenda Viva (301 pts)</span>
              <span className={stats.current_month_points >= 301 ? 'text-green-400' : 'text-slate-500'}>
                {stats.current_month_points >= 301 ? '✓' : `${301 - stats.current_month_points} pts restantes`}
              </span>
            </div>
            {stats.current_month_points >= 301 && (
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 w-full" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserStats;
